// ✅ src/pages/OrderManagement/ProductionInwardListing.jsx  (FULL FILE - UPDATED)
// ✅ Uses safe localStorage helper
// ✅ Adds finYearId, finYear, branchId, branchType, loginUserId
// ✅ Adds commonHeaders in API calls
// ✅ Loads ALL rows on page open using: POST /api/inwardListing/viewall
// ✅ Search uses: POST /api/inwardListing/search
// ✅ View navigates to entry page: /OrderManagement/Tar_Inward_Entry?TarInID=xx
// ✅ Print navigates: /OrderManagement/Tar_Inward_Print?ProdInID=xx

import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaEye,
  FaPrint,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/inwardListing";

// dd/MM/yyyy helpers
const pad2 = (n) => String(n).padStart(2, "0");
const toDMY = (d) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

const parseDDMMYYYY = (s) => {
  const str = String(s || "").trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yy = Number(m[3]);
  const dt = new Date(yy, mm - 1, dd);
  if (
    dt.getFullYear() !== yy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  )
    return null;
  return dt;
};

const toInt = (v, fallback = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

const toStr = (v, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  return String(v).trim();
};

export default function ProductionInwardReport() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [formData, setFormData] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return {
      fromDate: toDMY(from),
      toDate: toDMY(to),
      projectId: "0",
      productionId: "0",
    };
  });

  const [projects, setProjects] = useState([]);
  const [productions, setProductions] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalReceivedQty, setTotalReceivedQty] = useState(0);
  const [loadingDDL, setLoadingDDL] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const handleInputChange = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  const fetchDropdowns = async () => {
    try {
      setLoadingDDL(true);

      if (!branchId) {
        toast.error("branchId missing in localStorage");
        return;
      }

      if (!branchType) {
        toast.error("branchType missing in localStorage");
        return;
      }

      const [prjRes, prodRes] = await Promise.all([
        axios.get(`${API_BASE}/projects`, {
          params: { branchType, branchId },
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/productions`, {
          headers: commonHeaders,
        }),
      ]);

      setProjects(prjRes.data?.list || []);

      const prodList = prodRes.data?.list || [];
      const hasAll = prodList.some((x) => String(x.productionid) === "0");
      setProductions(
        hasAll
          ? prodList
          : [{ productionid: 0, productionname: "All" }, ...prodList]
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
    } finally {
      setLoadingDDL(false);
    }
  };

  // ✅ load ALL rows on open
  const fetchAllOnLoad = async () => {
    try {
      if (!branchId) {
        toast.error("branchId missing in localStorage");
        return;
      }

      setLoadingList(true);

      const payload = {
        branchId,
        projectId: "0",
        productionId: "0",
      };

      const res = await axios.post(`${API_BASE}/viewall`, payload, {
        headers: commonHeaders,
      });

      setRows(res.data?.list || []);
      setTotalReceivedQty(Number(res.data?.totalReceivedQty || 0));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load records");
      setRows([]);
      setTotalReceivedQty(0);
    } finally {
      setLoadingList(false);
    }
  };

  // ✅ filtered search
  const fetchList = async () => {
    try {
      const fd = parseDDMMYYYY(formData.fromDate);
      const td = parseDDMMYYYY(formData.toDate);

      if (!fd || !td) {
        toast.error("From/To Date must be dd/MM/yyyy");
        return;
      }

      if (fd > td) {
        toast.error("Please enter To Date greater than or equal to From Date");
        return;
      }

      if (!branchId) {
        toast.error("branchId missing in localStorage");
        return;
      }

      setLoadingList(true);

      const payload = {
        userId: loginUserId || undefined,
        branchId,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        projectId: formData.projectId,
        productionId: formData.productionId,
      };

      const res = await axios.post(`${API_BASE}/search`, payload, {
        headers: commonHeaders,
      });

      setRows(res.data?.list || []);
      setTotalReceivedQty(Number(res.data?.totalReceivedQty || 0));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load records");
      setRows([]);
      setTotalReceivedQty(0);
    } finally {
      setLoadingList(false);
    }
  };

  // ✅ Entry page uses TarInID in your app
  const getTarInId = (row) =>
    row?.TarInID ??
    row?.tarInId ??
    row?.fld_TarInID ??
    row?.fld_TarInId ??
    row?.inwardId ??
    row?.InwardId ??
    row?.id ??
    null;

  // ✅ Print page uses ProdInID
  const getProdInId = (row) =>
    row?.fld_ProdInID ??
    row?.ProdInID ??
    row?.prodInId ??
    row?.fld_ProdInId ??
    row?.ProdInId ??
    null;

  const handleEdit = (row) => {
    const tarInId = getTarInId(row);
    if (!tarInId) {
      console.log("EDIT ROW =>", row);
      toast.error("TarInID not found in row");
      return;
    }

    sessionStorage.setItem("TarInID", String(tarInId));
    navigate(`/OrderManagement/Tar_Inward_Entry?TarInID=${tarInId}`);
  };

  const handlePrint = (row) => {
    const prodInId = getProdInId(row);

    if (!prodInId) {
      console.log("PRINT ROW (no ProdInID) =>", row);
      toast.error("ProdInID not found in row. Check console log.");
      return;
    }

    sessionStorage.setItem("ProdInID", String(prodInId));

    navigate(`/OrderManagement/Tar_Inward_Print?ProdInID=${prodInId}`, {
      state: {
        row,
        ProdInID: prodInId,
      },
    });
  };

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, branchType]);

  useEffect(() => {
    if (branchId) fetchAllOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const inputBase =
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ` +
    (isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500");

  const labelBase = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION INWARD LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Production Inward Listing
              </h1>
              {(loadingDDL || loadingList) && (
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    isDark ? "bg-white/10" : "bg-gray-100"
                  }`}
                >
                  Loading...
                </span>
              )}
            </div>

            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className={labelBase}>From Date</label>
                    <input
                      type="text"
                      value={formData.fromDate}
                      onChange={(e) => handleInputChange("fromDate", e.target.value)}
                      className={inputBase}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>

                  <div>
                    <label className={labelBase}>Project Name</label>
                    <select
                      value={formData.projectId}
                      onChange={(e) => handleInputChange("projectId", e.target.value)}
                      className={inputBase}
                    >
                      <option value="0">Select</option>
                      {projects.map((p, idx) => {
                        const id =
                          p.fld_contractid ??
                          p.projectid ??
                          p.ProjectId ??
                          p.id ??
                          idx;
                        const name =
                          p.fld_contractname ??
                          p.projectname ??
                          p.ProjectName ??
                          p.text ??
                          "Project";
                        return (
                          <option key={String(id)} value={String(id)}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelBase}>To Date</label>
                    <input
                      type="text"
                      value={formData.toDate}
                      onChange={(e) => handleInputChange("toDate", e.target.value)}
                      className={inputBase}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>

                  <div>
                    <label className={labelBase}>Production Name</label>
                    <select
                      value={formData.productionId}
                      onChange={(e) => handleInputChange("productionId", e.target.value)}
                      className={inputBase}
                    >
                      {(productions.length
                        ? productions
                        : [{ productionid: 0, productionname: "All" }]
                      ).map((x, idx) => (
                        <option
                          key={String(x.productionid ?? idx)}
                          value={String(x.productionid ?? 0)}
                        >
                          {x.productionname ?? "All"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center items-center mt-6 gap-4">
                <button
                  onClick={fetchList}
                  disabled={loadingList}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loadingList ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  Search
                </button>
              </div>
            </div>
          )}

          <div className="mb-4 flex justify-end">
            <div
              className={`px-4 py-2 rounded-lg border text-sm font-semibold ${
                isDark
                  ? "bg-white/10 border-white/10 text-white"
                  : "bg-gray-50 border-gray-200 text-gray-900"
              }`}
            >
              Total Received Qty:{" "}
              <span className={`${isDark ? "text-white" : "text-gray-900"}`}>
                {Number(totalReceivedQty || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Production
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">
                      Received Qty
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className={isDark ? "divide-y divide-gray-700" : "divide-y divide-gray-200"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className={`p-14 text-center ${
                          isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"
                        }`}
                      >
                        {loadingList ? "Loading..." : "No Records found"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => {
                      const date =
                        r.fld_Date ??
                        r.Date ??
                        r.inwardDate ??
                        r.fld_InwardDate ??
                        "";
                      const project =
                        r.fld_projectname ??
                        r.ProjectName ??
                        r.projectName ??
                        r.fld_ProjectName ??
                        "";
                      const production =
                        r.productionname ??
                        r.ProductionName ??
                        r.productionName ??
                        r.fld_ProductionName ??
                        "";
                      const qty =
                        r.Recievedqty ??
                        r.RecievedQty ??
                        r.ReceivedQty ??
                        r.recievedqty ??
                        0;

                      return (
                        <tr key={i} className={isDark ? "bg-gray-900/20" : "bg-white"}>
                          <td className="px-4 py-3 text-sm">{i + 1}</td>
                          <td className="px-4 py-3 text-sm">{String(date)}</td>
                          <td className="px-4 py-3 text-sm">{String(project)}</td>
                          <td className="px-4 py-3 text-sm">{String(production)}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            {Number(qty || 0).toLocaleString("en-IN")}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(r)}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold shadow hover:shadow-md transition-all ${
                                  isDark
                                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                                    : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                                }`}
                                title="Edit / View"
                              >
                                <FaEye />
                                View
                              </button>

                              <button
                                onClick={() => handlePrint(r)}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold shadow hover:shadow-md transition-all ${
                                  isDark
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-green-100 hover:bg-green-200 text-green-700"
                                }`}
                                title="Print"
                              >
                                <FaPrint />
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
}